import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { Accordion, Text, Heading } from '@frontend/design-system';
import { Divider } from '../../../../../../../../components/divider';
import { toTitleCase } from '../../../../../../../../utils';
import { Row } from './row';

const isPlainObject = (value: DataValue): value is Record<string, DataValue> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const formatValue = (value: DataValue) => (value === null || value === undefined || value === '' ? '-' : String(value));

const sortedEntries = (value: Record<string, DataValue>, shouldSort = true) => {
  if (shouldSort) return Object.entries(value).sort(([a], [b]) => toTitleCase(a).localeCompare(toTitleCase(b)));
  return Object.entries(value);
};

type DataValue = string | number | boolean | null | undefined | DataValue[] | { [key: string]: DataValue };

type AdditionalDataProps = {
  data?: Record<string, DataValue>;
};

type DataEntryProps = {
  label: string;
  value: DataValue;
};

/**
 * NOTE: Only the first level objects are sorted, the nested objects maintain their original order.
 */
export const AdditionalData = ({ data }: AdditionalDataProps) => {
  const { t } = useTranslation('schedule-request');

  if (!data) return null;

  return (
    <>
      <Accordion variant='blank' chevronSize={16}>
        <Accordion.Item value={'additional-data'}>
          <Accordion.Header css={accordionHeaderOverrideStyles}>
            <Heading level={3}>{t('Additional Data')}</Heading>
          </Accordion.Header>
          <Accordion.Body css={accordionBodyOverrideStyles}>
            <div css={listStyles}>
              {sortedEntries(data).map(([key, value]) => (
                <DataEntry key={key} label={key} value={value} />
              ))}
            </div>
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>
      <Divider />
    </>
  );
};

const DataEntry = ({ label, value }: DataEntryProps) => {
  if (Array.isArray(value)) {
    return (
      <div css={listStyles}>
        <Text size='medium' color='light' weight='bold'>
          {toTitleCase(label)}
        </Text>
        {value.length ? (
          <div css={arrayStyles}>
            {value.map((item, index) =>
              isPlainObject(item) ? (
                <div key={index} css={arrayItemStyles}>
                  {sortedEntries(item, false).map(([itemKey, itemValue]) => (
                    <DataEntry key={itemKey} label={itemKey} value={itemValue} />
                  ))}
                </div>
              ) : (
                <Text key={index} size='medium' weight='bold' css={arrayItemStyles}>
                  {formatValue(item)}
                </Text>
              )
            )}
          </div>
        ) : (
          <Text size='medium'>-</Text>
        )}
      </div>
    );
  }

  if (isPlainObject(value)) {
    return (
      <div css={listStyles}>
        <Text size='medium' color='light' weight='bold'>
          {toTitleCase(label)}
        </Text>
        <div css={nestedStyles}>
          {sortedEntries(value, false).map(([childKey, childValue]) => (
            <DataEntry key={childKey} label={childKey} value={childValue} />
          ))}
        </div>
      </div>
    );
  }

  return <Row title={toTitleCase(label)} content={formatValue(value)} />;
};

const accordionHeaderOverrideStyles = css`
  border: none;
  & .header-container {
    padding: 0;
  }
`;

const accordionBodyOverrideStyles = css`
  display: flex;
  flex-direction: column;
  gap: var(--fabric-space-gap-xs);
  margin-top: var(--fabric-space-gap-md);
  padding: 0;
`;

const listStyles = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--fabric-space-gap-xs)',
});

const nestedStyles = css(listStyles, {
  paddingLeft: 'var(--fabric-space-gap-md)',
});

const arrayStyles = css(listStyles, {
  gap: 'var(--fabric-space-gap-sm)',
});

const arrayItemStyles = css(listStyles, {
  padding: 'var(--fabric-space-gap-sm)',
  borderRadius: 'var(--fabric-radius-sm)',
  backgroundColor: 'var(--fabric-color-neutral-5)',
  border: '1px solid var(--fabric-color-neutral-alpha-100-alpha-5)',
});
